import type {
  FoodActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"
import styles from "src/styles/entryModal.module.scss"

interface Props {
  activity?: DeepPartial<FoodActivity>;
  updateActivity: (a: DeepPartial<FoodActivity>) => void;
  readOnly?: boolean;
}

const id = "food-carbs-range-input"
const min = 0
const max = 300
const sliderStep = 5
const inputStep = 0.1
const polyglotPrefix = "components.entry_modal.food.carbs."

export function CarbsRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
    readOnly = false,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (carbs: number | null) => {
      updateActivity({ carbs })
    },
    [updateActivity],
  )

  const value = typeof activity?.carbs === "number"
    ? activity.carbs
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  return (
    <div className={styles.row}>
      <SliderValue sizePreset="large">
        <SliderValueInput
          min={min}
          max={max}
          step={inputStep}
          value={inputValue}
          id={"food-entry-modal-carbs-detail"}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
          readOnly={readOnly}
        />
        <SliderValueLabelContainer
          unit={polyglot.t(`${polyglotPrefix}label_unit`)}
        >
          <SliderValueLabel htmlFor={id}>
            {polyglot.t(`${polyglotPrefix}label_detail`)}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            min={min}
            max={max}
            step={sliderStep}
            id={id}
            value={sliderValue ?? min}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      )}
    </div>
  )
}
